import {
    createPageMetricsForResponse,
    PageType,
} from '@jet-app/music-classical/metrics/page';
import { ObjectGraph } from '@jet/environment/dependencies';
import {
    fixArtwork,
    makeVideoArtworkDictionary,
    type PageHeaderType,
    type PageScreenType,
} from '@jet-app/music-classical/models';
import type { ArtistPage } from '@jet-app/music-classical/models/pages/artist';
import type {
    ClassicalAPIResponse,
    ClassicalAPIResponseHeader,
} from '@jet-app/music-classical/api/types';
import { transformSections } from '@jet-app/music-classical/utilities/transforms/sections';
import { makeContextualMenuFetchAction } from '@jet-app/music-classical/action-model/contextual-menu/FetchAction';
import { addingArtistSeoData } from '@jet-app/music-classical/utilities/seo/Artist';
import type { ContextualMenuResponse } from '@jet-app/music-classical/utilities/should-render-contextual-menu';

export function renderArtistPage(
    canonicalURL: string,
    apiResponse: ClassicalAPIResponse,
    contextualMenuResponse: ContextualMenuResponse,
    objectGraph: ObjectGraph,
): ArtistPage {
    const responseHeader = apiResponse?.body
        ?.header as unknown as ClassicalAPIResponseHeader;

    const { entities, renderContextualMenu } = contextualMenuResponse;
    const page: ArtistPage = {
        kind: 'artistPage',
        header: {
            id: apiResponse?.body?.screenId,
            type: responseHeader?.type as PageHeaderType,
            title: responseHeader?.title,
            startYear: responseHeader?.startYear,
            endYear: responseHeader?.endYear,
            subtitle: responseHeader.subtitle,
            artwork: fixArtwork(responseHeader.image),
            // This is same url as uberArtwork[device].staticArtwork but regularImage has more properties
            uberArtwork: fixArtwork(responseHeader.regularImage),
            videoArtwork: makeVideoArtworkDictionary(
                responseHeader.uberArtwork,
            ),
            contextMenuAction: renderContextualMenu
                ? makeContextualMenuFetchAction(
                      apiResponse?.body?.userComponentsAction.url,
                  )
                : null,
            entities,
        },
        sections: transformSections(
            apiResponse?.body?.sections as any,
            objectGraph,
        ),
        screenType: apiResponse?.body.screenType as PageScreenType,
        canonicalURL,
        pageKind: 'componentScreen',
        pageMetrics: createPageMetricsForResponse(
            apiResponse,
            PageType.artistPage,
        ),
    };

    // Only render share button for artist pages if we aren't rendering a context menu.
    // The most likely case of this is viewing an artist page while signed out
    // OR that the contextual menu only has two items.
    if (!page.header.contextMenuAction || !renderContextualMenu) {
        page.header.shareButton = apiResponse?.body?.secondaryButtons?.find(
            (button) => button.subtype === 'share',
        );
        page.header.contextMenuAction = null;
    }

    return addingArtistSeoData(canonicalURL, page, objectGraph);
}
